import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaMapMarkerAlt, FaTrash, FaPlus, FaHeart } from 'react-icons/fa';

const FavoritesTab = ({ favorites, onLocationSelect, onRemoveFavorite, onClearAll }) => {
  if (favorites.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl mx-auto"
      >
        <div className="glass-card p-12 text-center">
          <div className="mb-6">
            <FaHeart className="text-6xl text-red-400 mx-auto mb-4 opacity-50" />
            <h2 className="text-3xl font-bold text-gray-800 mb-4">No Favorites Yet</h2>
            <p className="text-gray-600 text-lg mb-8">
              Start adding your favorite locations to get quick access to their weather information.
            </p>
          </div>
          
          <div className="bg-white/20 backdrop-blur-md rounded-2xl p-6 max-w-md mx-auto">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">How to add favorites:</h3>
            <div className="space-y-3 text-left">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                <span className="text-gray-700">Search for a location in the search bar</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                <span className="text-gray-700">Click the star icon to add to favorites</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                <span className="text-gray-700">View all your favorites here</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-6xl mx-auto"
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Your Favorites</h2>
          <p className="text-gray-600">{favorites.length} saved location{favorites.length !== 1 ? 's' : ''}</p>
        </div>
        
        {favorites.length > 0 && (
          <motion.button
            onClick={onClearAll}
            className="glass-button px-4 py-2 text-red-600 hover:text-red-700 flex items-center space-x-2"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaTrash className="text-sm" />
            <span className="text-sm font-medium">Clear All</span>
          </motion.button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence>
          {favorites.map((favorite, index) => (
            <motion.div
              key={favorite.id}
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="glass-card p-6 group hover:scale-105 transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-500/20 rounded-xl">
                    <FaMapMarkerAlt className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 text-lg">{favorite.name}</h3>
                    <p className="text-gray-500 text-sm">{favorite.country}</p>
                  </div>
                </div>
                
                <button
                  onClick={() => onRemoveFavorite(favorite.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-red-400 hover:text-red-600 p-1"
                >
                  <FaTrash className="text-sm" />
                </button>
              </div>

              <div className="space-y-3">
                <motion.button
                  onClick={() => onLocationSelect(favorite)}
                  className="w-full glass-button py-3 flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <FaPlus className="text-sm" />
                  <span className="font-medium">View Weather</span>
                </motion.button>
                
                <div className="text-xs text-gray-400 text-center">
                  Added {new Date(favorite.addedAt).toLocaleDateString()}
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default FavoritesTab;
